import { createContext, useContext, PropsWithChildren, useEffect, useState } from 'react';
import { Item, PromoCode } from '@root/src/types';
import { getPromotionalPrice, promoCodeAppliesToItem } from '@root/src/utils/promoCodes';
import { getVariantsOfBundle } from 'utils/utils';
import { RentOrBuyOptionsType } from 'components/BundleRadioPrices';

type Actions = {
  actions: {
    onPurchaseOptionChange: (option: RentOrBuyOptionsType) => void;
  };
};

type PurchaseOptionsContext = Actions & {
  selection: RentOrBuyOptionsType;
};

type AppContextStateValue = PurchaseOptionsContext & {
  promo: PromoCode | null;
};

type AppProviderProps = PropsWithChildren<{
  highlightedPromoCode: PromoCode | null;
}>;

const AppContext = createContext<AppContextStateValue>({
  actions: {
    onPurchaseOptionChange: () => {},
  },
  selection: 'Rent',
  promo: null,
});

export const AppProvider = ({ children, highlightedPromoCode }: AppProviderProps) => {
  const [state, setState] = useState<AppContextStateValue>({
    actions: {
      onPurchaseOptionChange: () => {},
    },
    selection: 'Rent',
    promo: highlightedPromoCode,
  });

  const actions = {
    onPurchaseOptionChange: (option: RentOrBuyOptionsType) => {
      setState({
        ...state,
        selection: option,
      });
    },
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        actions: actions,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export type PromotionalPricingForBundle = {
  itemType: 'bundle';
  promoCode: string;
  rentalValue: string | null;
  retailValue: string | null;
};

export type PromotionalPricingForProduct = {
  itemType: 'product';
  promoCode: string;
  value: string;
};

export type PromotionalPricing = PromotionalPricingForBundle | PromotionalPricingForProduct;

/**
 * Returns promotional pricing data for the given item, relative to the promo code
 * that's currently highlighted across the site, if there is one.
 *
 * This hook will return `null` in the following cases:
 *
 *  1. If no promo code is configured to be highlighted across the site, or
 *  2. If the highlighted promo code is inactive, or
 *  3. If the highlighted promo code doesn't apply to the item
 *
 * When the given item is a bundle, `null` will only be returned if the promo code
 * applies to neither of the variants of that bundle, assuming there are two.
 * For example, if the item is a rental bundle that the promo code *doesn't* apply
 * a discount to, but it has a retail variant that the promo code *does* affect,
 * then a `PromotionalPricing` object will be returned. If neither the rental nor
 * retail variants of that bundle were applicable for the discount, then `null`
 * would be returned.
 */
export const usePromotionalPricing = (item: Item): PromotionalPricing | null => {
  const highlightedPromoCode = useContext(AppContext).promo;

  const [promotionalPricing, setPromotionalPricing] = useState<PromotionalPricing | null>(null);

  useEffect(() => {
    if (!highlightedPromoCode) {
      return;
    }

    const isBundle = item.category === 'preconfigured';

    if (!isBundle && promoCodeAppliesToItem(item, highlightedPromoCode)) {
      return setPromotionalPricing({
        itemType: 'product',
        promoCode: highlightedPromoCode.code,
        value: getPromotionalPrice(item, highlightedPromoCode),
      });
    }

    const { rentalBundle, retailBundle } = getVariantsOfBundle(item);

    const appliesToRental = rentalBundle ? promoCodeAppliesToItem(rentalBundle, highlightedPromoCode) : false;
    const appliesToRetail = retailBundle ? promoCodeAppliesToItem(retailBundle, highlightedPromoCode) : false;

    if (!appliesToRental && !appliesToRetail) {
      return;
    }

    setPromotionalPricing({
      itemType: 'bundle',
      promoCode: highlightedPromoCode.code,
      rentalValue: appliesToRental ? getPromotionalPrice(rentalBundle!, highlightedPromoCode) : null,
      retailValue: appliesToRetail ? getPromotionalPrice(retailBundle!, highlightedPromoCode) : null,
    });
  }, [highlightedPromoCode, item]);

  return promotionalPricing;
};

export const usePurchaseOptionSelection = () => useContext(AppContext) as PurchaseOptionsContext;
